import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { provider, useInstance } from "react-ioc";
import { ListingsStore } from "app/modules/main/components/admin/integration/listings/ListingsStore";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Loading } from "app/modules/common/layout/components/loading";
import { HostToolsListingSummary, HostToolsPropertyOption } from "app/modules/main/api/admin-clients.api";

const IntegrationListingsContent = observer(() => {
    const store = useInstance(ListingsStore);
    const [linkTarget, setLinkTarget] = useState<HostToolsListingSummary>(null);
    const [selectedPropertyId, setSelectedPropertyId] = useState<number>(null);

    useEffect(() => {
        store.init();
    }, [store]);

    useEffect(() => {
        if (linkTarget?.propertyId) {
            setSelectedPropertyId(linkTarget.propertyId);
        } else {
            setSelectedPropertyId(null);
        }
    }, [linkTarget]);

    const integrationOptions = useMemo(
        () =>
            store.integrations?.map(integration => (
                <MenuItem key={integration.id} value={integration.id}>
                    {integration.name} (#{integration.id})
                </MenuItem>
            )) ?? [],
        [store.integrations]
    );

    const availablePropertyOptions: HostToolsPropertyOption[] = useMemo(
        () => store.propertyOptions ?? [],
        [store.propertyOptions]
    );

    if (store.isInitLoading) {
        return <Loading />;
    }

    if (!store.hasHosttoolsIntegrations) {
        return (
            <Alert severity="info">
                No active Host Tools API integrations found. Add a Host Tools integration to start syncing listings.
            </Alert>
        );
    }

    const listingItems = store.listings ?? [];

    return (
        <Stack spacing={3}>
            <Box display="flex" alignItems="center" gap={2}>
                <FormControl sx={{ minWidth: 240 }} size="small">
                    <InputLabel id="integration-select-label">Integration</InputLabel>
                    <Select
                        labelId="integration-select-label"
                        label="Integration"
                        value={store.selectedIntegrationId ?? ""}
                        onChange={async event => {
                            store.setSelectedIntegration(Number(event.target.value));
                            await store.loadListings();
                        }}
                    >
                        {integrationOptions}
                    </Select>
                </FormControl>
                <Button variant="outlined" onClick={store.loadListings} disabled={store.selectedIntegrationId == null || store.isLoading}>
                    Refresh
                </Button>
                {store.isLoading && <CircularProgress size={24} />}
            </Box>

            {listingItems.length === 0 && (
                <Alert severity="info">No Host Tools listings found for this integration.</Alert>
            )}

            <Stack spacing={2}>
                {listingItems.map(listing => {
                    const host = (listing.hostListing ?? {}) as {
                        address?: string;
                        city?: string;
                        state?: string;
                        zip_code?: string;
                        zipCode?: string;
                        property_type?: string;
                        base_price?: number;
                    };
                    const isLinked = listing.propertyId != null;
                    const propertyLabel = isLinked
                        ? `${listing.propertyName ?? listing.propertyInternalName ?? `Property #${listing.propertyId}`}`
                        : "Not linked";

                    const linkedChipColor = listing.isIgnored ? "warning" : isLinked ? "success" : "default";
                    const linkedChipLabel = listing.isIgnored ? "Ignored" : isLinked ? "Linked" : "Pending";

                    return (
                        <Accordion key={listing.syncId} disabled={store.isLoading}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" flexDirection="column" flexGrow={1}>
                                    <Typography variant="subtitle1">{listing.name ?? listing.listingId}</Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Chip label={linkedChipLabel} color={linkedChipColor as any} size="small" />
                                        <Typography variant="body2" color="text.secondary">
                                            Status: {listing.status ?? "Unknown"}
                                        </Typography>
                                        {listing.lastSeenAt && (
                                            <Typography variant="body2" color="text.secondary">
                                                Last seen: {listing.lastSeenAt.format("YYYY-MM-DD HH:mm")}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="subtitle2">Linked Property</Typography>
                                        <Typography variant="body2">
                                            {propertyLabel}
                                            {isLinked && listing.propertyStatus && (
                                                <Chip
                                                    sx={{ ml: 1 }}
                                                    size="small"
                                                    label={PropertyStatusLabel(listing.propertyStatus)}
                                                    color={listing.propertyStatus === 1 ? "success" : listing.propertyStatus === 2 ? "default" : "warning"}
                                                />
                                            )}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2">Ignore listing</Typography>
                                        <Switch
                                            checked={listing.isIgnored ?? false}
                                            onChange={(_, checked) => store.setIgnore(listing.syncId, checked)}
                                        />
                                    </Box>

                                    <Divider />

                                    <Box>
                                        <Typography variant="subtitle2">Host Tools Details</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Listing ID: {listing.listingId}
                                        </Typography>
                                        {host.address && (
                                            <Typography variant="body2" color="text.secondary">
                                                Address: {host.address}
                                                {host.city ? `, ${host.city}` : ""}
                                                {host.state ? `, ${host.state}` : ""} {host.zip_code ?? host.zipCode ?? ""}
                                            </Typography>
                                        )}
                                        {host.property_type && (
                                            <Typography variant="body2" color="text.secondary">
                                                Property Type: {host.property_type}
                                            </Typography>
                                        )}
                                        {host.base_price && (
                                            <Typography variant="body2" color="text.secondary">
                                                Base Price: ${host.base_price}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => setLinkTarget(listing)}
                                            disabled={store.propertyOptions.length === 0}
                                        >
                                            Link to property
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => store.createPropertyForListing(listing.syncId)}
                                            disabled={listing.propertyId != null}
                                        >
                                            Create property
                                        </Button>
                                    </Box>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>

            <Dialog
                fullWidth
                maxWidth="sm"
                open={linkTarget != null}
                onClose={() => setLinkTarget(null)}
            >
                <DialogTitle>Link Host Tools Listing</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Host listing: {linkTarget?.name ?? linkTarget?.listingId}
                        </Typography>
                        <FormControl fullWidth size="small">
                            <InputLabel id="property-select-label">Property</InputLabel>
                            <Select
                                labelId="property-select-label"
                                label="Property"
                                value={selectedPropertyId ?? ""}
                                onChange={event => setSelectedPropertyId(Number(event.target.value))}
                            >
                                {availablePropertyOptions.map(option => (
                                    <MenuItem
                                        key={option.propertyId}
                                        value={option.propertyId}
                                        disabled={
                                            option.isLinked &&
                                            option.linkedListingId &&
                                            option.linkedListingId !== linkTarget?.listingId
                                        }
                                    >
                                        {option.name ?? option.internalName ?? `Property #${option.propertyId}`}
                                        {option.isLinked && option.linkedListingId && option.linkedListingId !== linkTarget?.listingId
                                            ? " (Already linked)"
                                            : ""}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLinkTarget(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!selectedPropertyId}
                        onClick={async () => {
                            await store.linkListingToProperty(linkTarget.syncId, selectedPropertyId);
                            setLinkTarget(null);
                            setSelectedPropertyId(null);
                            await store.refreshPropertyOptions();
                        }}
                    >
                        Link
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
});

const PropertyStatusLabel = (status: number): string => {
    switch (status) {
        case 1:
            return "Listed";
        case 2:
            return "Unlisted";
        case 3:
            return "Deactivated";
        default:
            return "Unknown";
    }
};

export default provider(ListingsStore)(IntegrationListingsContent);

